"use client"

import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"

import { cn } from "@/lib/utils"

/**
 * The app's one tooltip. Every hover or focus label goes through this file:
 * `SimpleTooltip` for a label on a control (the common case, and what replaces
 * a `title` attribute), or the compound parts for a controlled tooltip or a
 * custom trigger. One `TooltipProvider` in `app/layout.js` sets the delay for
 * the whole app; never add another. Not for rich hover previews (that is
 * `HoverCard`), chart tooltips (recharts), or the Guide floating window, which
 * is its own document a portal cannot reach (`TruncatedText nativeTitle`).
 * `tests/unit/tooltipPrimitive.test.js` fails on a `title` tooltip, a second
 * provider, or another tooltip library.
 */

const TOOLTIP_DELAY_MS = 300

const HasProvider = React.createContext(false)

function TooltipProvider({ delayDuration = TOOLTIP_DELAY_MS, children, ...props }) {
  return (
    <TooltipPrimitive.Provider delayDuration={delayDuration} {...props}>
      <HasProvider.Provider value>{children}</HasProvider.Provider>
    </TooltipPrimitive.Provider>
  )
}

// A tree mounted on its own (a unit test, a portal into another root) has no
// app provider above it; give it the same one rather than throwing.
function Tooltip(props) {
  const root = <TooltipPrimitive.Root {...props} />
  return React.useContext(HasProvider) ? root : <TooltipProvider>{root}</TooltipProvider>
}

const TooltipTrigger = TooltipPrimitive.Trigger

// Portalled to the body, so a tooltip escapes the container its trigger sits
// in: the side nav's cream column clips its overflow, and a sticky page
// outline painted later would otherwise cover anything that spilled out.
const TooltipContent = React.forwardRef(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "z-50 max-w-xs overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-xs leading-relaxed text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className
      )}
      {...props} />
  </TooltipPrimitive.Portal>
))
TooltipContent.displayName = TooltipPrimitive.Content.displayName

/**
 * `<SimpleTooltip content="Delete"><Button …/></SimpleTooltip>` — the
 * replacement for `title="Delete"`. No content renders the child alone, so a
 * conditional label needs no branch at the call site. A disabled child gets a
 * focusable wrapper, because a disabled button fires no pointer events and
 * its tooltip (often the reason it is disabled) would never open. A tooltip
 * is not an accessible name: an icon-only control still needs `aria-label`.
 */
function SimpleTooltip({ content, side, align, className, children }) {
  if (content === undefined || content === null || content === false || content === "") {
    return children
  }
  const trigger = React.isValidElement(children) && children.props.disabled ? (
    <span tabIndex={0} className="inline-flex">
      {children}
    </span>
  ) : (
    children
  )
  return (
    <Tooltip>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      <TooltipContent side={side} align={align} className={className}>
        {content}
      </TooltipContent>
    </Tooltip>
  )
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, SimpleTooltip }
